import { PROFILE_VIEW_SECTION } from '@workspace/seedforge-shared'
import { FileText } from 'lucide-react'
import { Section } from '../components/section.js'
import type { DataRoomDocument, ProfileUpdate } from '../types.js'

export interface DocumentsSectionProps {
  pitchDeck?: DataRoomDocument | null
  documents: DataRoomDocument[]
  updates?: ProfileUpdate[]
  onDocumentClick?: (doc: DataRoomDocument) => void
}

export function DocumentsSection({
  pitchDeck,
  documents,
  updates = [],
  onDocumentClick,
}: DocumentsSectionProps) {
  if (!pitchDeck && documents.length === 0 && updates.length === 0) return null

  return (
    <Section dataSection={PROFILE_VIEW_SECTION.DOCUMENTS}>
      <div className="space-y-4">
        {pitchDeck && (
          <div>
            <h2 className="mb-3 text-xs font-medium uppercase tracking-wider text-muted-foreground">
              Pitch Deck
            </h2>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
              <DocumentCard doc={pitchDeck} onClick={() => onDocumentClick?.(pitchDeck)} />
            </div>
          </div>
        )}
        <div>
          <h2 className="mb-3 text-xs font-medium uppercase tracking-wider text-muted-foreground">
            Documents
          </h2>
          {documents.length > 0 ? (
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {documents.map((doc) => (
                <DocumentCard
                  key={doc.documentId ?? doc.name}
                  doc={doc}
                  onClick={() => onDocumentClick?.(doc)}
                />
              ))}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground/50">No documents</p>
          )}
        </div>
        {updates.length > 0 && (
          <div>
            <h2 className="mb-3 text-xs font-medium uppercase tracking-wider text-muted-foreground">
              Updates
            </h2>
            <div className="space-y-3">
              {updates.map((update) => (
                <div
                  key={update.id}
                  data-update={update.text}
                  className="rounded-lg bg-card px-4 py-3.5"
                >
                  <div className="text-xs text-muted-foreground/70">{updateLabel(update.date)}</div>
                  <p className="mt-1 whitespace-pre-line text-sm text-white">{update.text}</p>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </Section>
  )
}

/** "Jul 8, from an email update"; the year only when it is not this one. */
function updateLabel(value: string) {
  const date = new Date(value)
  const sameYear = date.getFullYear() === new Date().getFullYear()
  const day = date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    ...(sameYear ? {} : { year: 'numeric' }),
  })
  return `${day}, from an email update`
}

function DocumentCard({ doc, onClick }: { doc: DataRoomDocument; onClick?: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex cursor-pointer items-center gap-3 rounded-lg bg-card px-4 py-3.5 text-left transition-colors hover:bg-hover"
    >
      <FileText className="size-5 shrink-0 text-[#f97316]" />
      <div className="min-w-0 flex-1">
        <div className="truncate text-sm font-medium text-white">{doc.name}</div>
        {doc.size && <div className="mt-0.5 text-xs text-muted-foreground/50">{doc.size}</div>}
      </div>
    </button>
  )
}
